import AppCard from '@nxsys/components/AppCard';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Option } from '@nxsys/types/models/Common';
import { countryList } from '@nxsys/utils';
import type { FormProps } from 'antd';
import { Col, Form, Row } from 'antd';
import React, { Ref, useImperativeHandle, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  defaultCurrentCyOption,
  paymentModelOptionsIreland,
  yesNoNAOptions,
  yesNoOptions,
} from '@nxsys/constants/AppConstants';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import { PlusOutlined } from '@ant-design/icons';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { showMessage } from '@nxsys/utils/message';
import { validateEmail, validatePhone } from '@nxsys/utils/validator';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import { useParams } from 'react-router-dom';
import {
  formatPostCode,
  postCodeValidationRule,
} from '@nxsys/utils/formFieldValidation';
import { YesNoNAEnum } from '@nxsys/constants/AppEnums';
import AppRateInput from '@nxsys/components/AppRateInput';

export type AgencyFormValues = {
  agencyName?: string;
  timesheetApprovalNeeded?: string;
  timesheetApprover?: Option<string>[];
  allowPortalAccess?: boolean | null;
  enableBenefits?: boolean | null;
  paymentModel?: Option<string>[];
  agencyPrefix?: string;
  currency?: Option<string> | null;
  convertRate?: string;
  emergencyContactName?: string;
  emergencyEmail?: string;
  emergencyMobile?: string;
  emergencyTelephone?: string;
  contactDetails: {
    name: string;
    email: string;
    mobile: string;
    address1: string;
    address2: string;
    address3: string;
    city: string;
    county: string;
    postCode: string;
    country: Option<string> | null;
    onboardingEmailNotification: string[];
  };
};

export type AgencyFormRef = {
  submit: () => void;
};

type AgencyFormProps = {
  defaultValues?: Partial<AgencyFormValues>;
  formRef?: Ref<AgencyFormRef>;
  onSubmit?: (values: AgencyFormValues) => void;
  submitting?: boolean;
  agencyId?: string;
};

const innerDefaultValues = {
  agencyName: '',
  timesheetApprovalNeeded: 'NOT_AVAILABLE',
  timesheetApprover: [],
  allowPortalAccess: null,
  enableBenefits: null,
  paymentModel: [],
  emergencyContactName: '',
  emergencyEmail: '',
  emergencyMobile: '',
  emergencyTelephone: '',
  agencyPrefix: '',
  currency: null,
  convertRate: '',
  contactDetails: {
    name: '',
    email: '',
    mobile: '',
    address1: '',
    address2: '',
    address3: '',
    city: '',
    county: '',
    postCode: '',
    country: null,
    onboardingEmailNotification: [],
  },
};

const AgencyForm: React.FC<AgencyFormProps> = ({
  defaultValues = innerDefaultValues,
  onSubmit,
  formRef,
  agencyId: agencyIdProp,
}: AgencyFormProps) => {
  const { payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const params = useParams();
  const agencyId = agencyIdProp || params.agencyId;

  const { messages } = useTypedIntl();
  const [form] = Form.useForm<AgencyFormValues>();
  const watchValues = Form.useWatch([], form) ?? {};

  const appStore = useSelector((state: AppState) => state.appStore);
  const defaultPaymentModel = appStore?.appConfigs?.paymentModels;

  const paymentModelOptions = useMemo(() => {
    return isIreland ? paymentModelOptionsIreland : (defaultPaymentModel ?? []);
  }, [defaultPaymentModel, isIreland]);

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
  }));

  const onFinish: FormProps<AgencyFormValues>['onFinish'] = (values) => {
    onSubmit?.({
      ...values,
      contactDetails: {
        ...values.contactDetails,
        onboardingEmailNotification: onboardingEmailList.map(
          (item) => item.value,
        ),
      },
    });
  };

  const onFinishFailed: FormProps<AgencyFormValues>['onFinishFailed'] = (
    errorInfo,
  ) => {
    console.log('Failed:', errorInfo);
  };

  const [onboardingEmailValue, setOnboardingEmailValue] = useState<string>('');
  const [onboardingEmailList, setOnboardingEmailList] = useState<
    Option<string>[]
  >(
    (defaultValues?.contactDetails?.onboardingEmailNotification ?? []).map(
      (item) => ({
        label: item,
        value: item,
      }),
    ),
  );
  const handleAddOnboardingEmail = (email: string) => {
    if (!validateEmail(email)) {
      showMessage({
        type: 'error',
        content: messages['form.enterValidEmail'],
      });
      return;
    }
    if (onboardingEmailList.some((item) => item.value === email)) {
      showMessage({
        type: 'error',
        content: messages['common.emailAlreadyAdded'],
      });
      return;
    }
    setOnboardingEmailList([
      ...onboardingEmailList,
      { label: email, value: email },
    ]);
    setOnboardingEmailValue('');
  };

  return (
    <div className="space-y-6">
      <Row gutter={32}>
        {/* General Details */}
        <Col span={12} className="space-y-6">
          <AppCard title={messages['agency.create.generalDetails']}>
            <AppFormContainer
              form={form}
              initialValues={defaultValues}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              labelCol={{ span: 10 }}
              wrapperCol={{ span: 14 }}
              className="px-4 pt-0 pb-2"
            >
              <Form.Item
                label={messages['assignment.detail.agencyName']}
                name="agencyName"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <AppInput
                  placeholder={messages['agency.create.enterAgencyName']}
                />
              </Form.Item>

              <Form.Item
                label={messages['agency.create.timesheetApprovalNeeded']}
                name="timesheetApprovalNeeded"
                valuePropName="value"
              >
                <AppRadioGroup options={yesNoNAOptions} />
              </Form.Item>

              {watchValues.timesheetApprovalNeeded === YesNoNAEnum.YES && (
                <Form.Item
                  label={messages['common.timesheetApprover']}
                  name="timesheetApprover"
                >
                  <AppSelectSync
                    type="approver"
                    apiType="searchAgencyUsers"
                    placeholder={messages['form.select']}
                    style={{ width: '100%' }}
                    searchId={agencyId}
                    mode="multiple"
                    onChangeValue={(value: any) => {
                      form.setFieldValue('timesheetApprover', value);
                    }}
                  />
                </Form.Item>
              )}

              <Form.Item
                label={messages['agency.create.allowPortalAccess']}
                name="allowPortalAccess"
                valuePropName="value"
              >
                <AppRadioGroup options={yesNoOptions} />
              </Form.Item>

              <Form.Item
                label={messages['agency.create.agencyPrefix']}
                name="agencyPrefix"
              >
                <AppInput
                  placeholder={messages['agency.create.enterAgencyPrefix']}
                />
              </Form.Item>

              <Form.Item
                label={messages['agency.create.currency']}
                name="currency"
              >
                <AppSelect
                  options={defaultCurrentCyOption}
                  className="w-full !min-w-0"
                />
              </Form.Item>

              <Form.Item
                label={messages['agency.create.paymentModel']}
                name="paymentModel"
              >
                <AppSelect
                  options={paymentModelOptions}
                  placeholder={messages['form.select']}
                  mode="multiple"
                  maxTagCount="number"
                  className="w-full !min-w-0"
                />
              </Form.Item>

              <Form.Item
                label={messages['agency.create.enableBenefits']}
                name="enableBenefits"
                valuePropName="value"
              >
                <AppRadioGroup options={yesNoOptions} />
              </Form.Item>

              {(watchValues?.currency?.value === 'EUR' ||
                watchValues?.currency?.value === 'USD') && (
                <Form.Item
                  label={messages['agency.create.convertRate']}
                  name="convertRate"
                >
                  <AppRateInput
                    placeholder={messages['agency.create.enterConvertRate']}
                  />
                </Form.Item>
              )}
            </AppFormContainer>
          </AppCard>

          {/* Emergency Contact */}
          <AppCard title={messages['agency.create.emergencyContact']}>
            <AppFormContainer
              form={form}
              initialValues={defaultValues}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              labelCol={{ span: 10 }}
              wrapperCol={{ span: 14 }}
              className="px-4 pt-0 pb-2"
            >
              <Form.Item
                label={messages['agency.create.enterContactName']}
                name="emergencyContactName"
              >
                <AppInput
                  placeholder={messages['agency.create.enterContactName']}
                />
              </Form.Item>

              <Form.Item
                label={messages['common.email']}
                name="emergencyEmail"
                rules={[
                  {
                    type: 'email',
                    message: messages['form.enterValidEmail'],
                  },
                ]}
              >
                <AppInput
                  placeholder={messages['agency.create.enterEmail']}
                  allowCapitalize={false}
                />
              </Form.Item>

              <Form.Item
                label={messages['agency.create.telephone']}
                name="emergencyTelephone"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      if (!validatePhone(value)) {
                        return Promise.reject(
                          messages['form.enterValidMobile'],
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <AppRateInput
                  placeholder={messages['agency.create.enterTelephone']}
                />
              </Form.Item>

              <Form.Item
                label={messages['agency.create.mobile']}
                name="emergencyMobile"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      if (!validatePhone(value)) {
                        return Promise.reject(
                          messages['form.enterValidMobile'],
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <AppRateInput
                  placeholder={messages['agency.create.enterMobile']}
                />
              </Form.Item>
            </AppFormContainer>
          </AppCard>
        </Col>

        {/* Contact Detail */}
        <Col span={12} className="space-y-6">
          {!isIreland && (
            <AppCard title={messages['agency.create.contactDetail']}>
              <AppFormContainer
                form={form}
                initialValues={defaultValues}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                labelCol={{ span: 10 }}
                wrapperCol={{ span: 14 }}
                className="px-4 pt-0 pb-2"
              >
                <Form.Item
                  label={messages['common.contactName']}
                  name={['contactDetails', 'name']}
                >
                  <AppInput
                    placeholder={messages['agency.create.enterContactName']}
                  />
                </Form.Item>

                <Form.Item
                  label={messages['agency.create.address1']}
                  name={['contactDetails', 'address1']}
                  rules={[
                    {
                      required: true,
                      message: messages['form.pleaseInput'],
                    },
                  ]}
                >
                  <AppInput
                    placeholder={messages['agency.create.enterAddress1']}
                  />
                </Form.Item>

                <Form.Item
                  label={messages['agency.create.address2']}
                  name={['contactDetails', 'address2']}
                  rules={[
                    {
                      required: true,
                      message: messages['form.pleaseInput'],
                    },
                  ]}
                >
                  <AppInput
                    placeholder={messages['agency.create.enterAddress2']}
                  />
                </Form.Item>

                <Form.Item
                  label={messages['agency.create.address3']}
                  name={['contactDetails', 'address3']}
                >
                  <AppInput
                    placeholder={messages['agency.create.enterAddress3']}
                  />
                </Form.Item>

                <Form.Item
                  label={messages['agency.create.city']}
                  name={['contactDetails', 'city']}
                >
                  <AppInput placeholder={messages['agency.create.enterCity']} />
                </Form.Item>

                <Form.Item
                  label={messages['common.county']}
                  name={['contactDetails', 'county']}
                >
                  <AppInput
                    placeholder={messages['agency.create.enterCounty']}
                  />
                </Form.Item>

                <Form.Item
                  label={
                    messages[
                      isIreland ? 'common.eirCode' : 'agency.create.postCode'
                    ]
                  }
                  name={['contactDetails', 'postCode']}
                  rules={[
                    { required: true },
                    postCodeValidationRule(messages, payrollCountry),
                  ]}
                >
                  <AppInput
                    placeholder={
                      messages[
                        isIreland
                          ? 'agency.create.enterEirCode'
                          : 'agency.create.enterPostCode'
                      ]
                    }
                    upperCaseAll
                    onChange={(e) => {
                      form.setFieldValue(
                        ['contactDetails', 'postCode'],
                        formatPostCode(e.target.value, payrollCountry),
                      );
                    }}
                    maxLength={8}
                  />
                </Form.Item>

                <Form.Item
                  label={messages['agency.create.country']}
                  name={['contactDetails', 'country']}
                >
                  <AppSelect
                    placeholder={messages['agency.create.selectCountry']}
                    options={countryList}
                    className="w-full !min-w-0"
                  />
                </Form.Item>

                <Form.Item
                  label={messages['agency.create.mobile']}
                  name={['contactDetails', 'mobile']}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        if (!validatePhone(value)) {
                          return Promise.reject(
                            messages['form.enterValidMobile'],
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <AppRateInput
                    placeholder={messages['agency.create.enterMobile']}
                  />
                </Form.Item>

                <Form.Item
                  label={messages['common.emailId']}
                  name={['contactDetails', 'email']}
                  rules={[
                    {
                      required: true,
                      message: messages['form.pleaseInput'],
                    },
                    {
                      type: 'email',
                      message: messages['form.enterValidEmail'],
                    },
                  ]}
                >
                  <AppInput
                    placeholder={messages['agency.create.enterEmail']}
                    allowCapitalize={false}
                  />
                </Form.Item>

                <Form.Item label={messages['agency.create.onboardingEmail']}>
                  <AppSelect
                    options={[]}
                    placeholder={messages['agency.create.enterEmail']}
                    mode="multiple"
                    className="w-full !min-w-0"
                    open={false}
                    autoClearSearchValue={false}
                    allowClear={false}
                    allowCapitalize={false}
                    suffixIcon={
                      <PlusOutlined
                        onClick={() =>
                          handleAddOnboardingEmail(onboardingEmailValue)
                        }
                        style={{
                          color: defaultTheme.theme.palette.primary[600],
                        }}
                      />
                    }
                    searchValue={onboardingEmailValue}
                    onSearch={(value: string) => setOnboardingEmailValue(value)}
                    value={onboardingEmailList}
                    onDeselect={(option: any) => {
                      setOnboardingEmailList(
                        onboardingEmailList.filter(
                          (item) => item.value !== option.value,
                        ),
                      );
                    }}
                  />
                </Form.Item>
              </AppFormContainer>
            </AppCard>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default AgencyForm;
